import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Box,
  Button,
  IconButton,
  Chip,
  LinearProgress,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  MoreVert,
  Visibility,
  Chat,
  Delete,
  GetApp,
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
  Refresh,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { DeckSummary } from '../../types';
import { formatDateTime } from '../../utils/formatting';
import { ReanalysisDialog } from './ReanalysisDialog';

interface DeckCardProps {
  deck: DeckSummary;
  onDelete?: (deckId: string) => void;
  onReanalyzeSuccess?: () => void;
}

export const DeckCard: React.FC<DeckCardProps> = ({ deck, onDelete, onReanalyzeSuccess }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [reanalysisDialogOpen, setReanalysisDialogOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    if (onDelete) {
      onDelete(deck.deckId);
    }
  };

  const handleReanalyze = (event: React.MouseEvent) => {
    event.stopPropagation();
    handleMenuClose();
    setReanalysisDialogOpen(true);
  };

  const getStatusConfig = () => {
    switch (deck.status) {
      case 'completed':
        return {
          label: 'Completed',
          color: 'success' as const,
          icon: <CheckCircle />,
        };
      case 'failed':
        return {
          label: 'Failed',
          color: 'error' as const,
          icon: <ErrorIcon />,
        };
      case 'processing':
      case 'converting':
        return {
          label: 'Processing',
          color: 'warning' as const,
          icon: <HourglassEmpty />,
        };
      default:
        return {
          label: 'Pending',
          color: 'default' as const,
          icon: <HourglassEmpty />,
        };
    }
  };

  const statusConfig = getStatusConfig();
  const isProcessing = deck.status === 'processing' || deck.status === 'converting';

  return (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 4,
          transform: 'translateY(-4px)',
        },
      }}
      onClick={() => navigate(`/analysis/${deck.deckId}`)}
    >
      <CardContent sx={{ flexGrow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Typography 
            variant="h6" 
            component="div" 
            title={deck.fileName}
            sx={{ 
              flex: 1, 
              pr: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {deck.fileName}
          </Typography>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{ ml: 1 }}
          >
            <MoreVert />
          </IconButton>
        </Box>

        <Chip
          icon={statusConfig.icon}
          label={statusConfig.label}
          color={statusConfig.color}
          size="small"
          sx={{ mb: 2 }}
        />

        {isProcessing && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
          </Box>
        )}

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Uploaded: {formatDateTime(deck.uploadedAt)}
        </Typography>

        {deck.totalSlides !== undefined && (
          <Typography variant="body2" color="text.secondary">
            Slides: {deck.totalSlides}
          </Typography>
        )}

        {deck.issueCount && (
          <Box display="flex" gap={1} mt={2} flexWrap="wrap">
            <Chip 
              label={`Critical: ${deck.issueCount.critical}`} 
              size="small" 
              color="error"
              variant="outlined"
            />
            <Chip 
              label={`High: ${deck.issueCount.high}`} 
              size="small" 
              color="error"
              variant="outlined"
            />
            <Chip 
              label={`Low: ${deck.issueCount.low}`} 
              size="small" 
              color="info"
              variant="outlined"
            />
          </Box>
        )}
      </CardContent>

      <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
        <Button
          size="small"
          startIcon={<Visibility />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/analysis/${deck.deckId}`);
          }}
        >
          View
        </Button>
        <Button
          size="small"
          startIcon={<Chat />}
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/chat/${deck.deckId}`);
          }}
          disabled={deck.status !== 'completed'}
          sx={{ display: 'none' }}
        >
          Chat
        </Button>
      </CardActions>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={(e) => {
          e.stopPropagation();
          handleMenuClose();
          navigate(`/analysis/${deck.deckId}`);
        }}>
          <ListItemIcon>
            <Visibility fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        <MenuItem onClick={(e) => {
          e.stopPropagation();
          handleMenuClose();
          navigate(`/chat/${deck.deckId}`);
        }} disabled={deck.status !== 'completed'} sx={{ display: 'none' }}>
          <ListItemIcon>
            <Chat fontSize="small" />
          </ListItemIcon>
          <ListItemText>Chat</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={handleReanalyze}
          disabled={deck.status === 'processing' || deck.status === 'converting'}
        >
          <ListItemIcon>
            <Refresh fontSize="small" />
          </ListItemIcon>
          <ListItemText>Re-analyze Deck</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDelete}>
          <ListItemIcon>
            <Delete fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      <ReanalysisDialog
        open={reanalysisDialogOpen}
        onClose={() => setReanalysisDialogOpen(false)}
        deck={deck}
        onSuccess={onReanalyzeSuccess}
      />
    </Card>
  );
};

